import { ArrowBack } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import BannerDataTable from './components/BannerDataTable';
import InputField from 'packages/novous_web/src/common/components/InputField';
import CustomDateTimePicker from 'packages/novous_web/src/common/components/CustomDateTimePicker';
import Button from 'packages/novous_web/src/common/components/Button';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import dayjs from 'dayjs';
import useApiError from 'packages/novous_web/src/api/useApiError';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import { MuiColorInput } from 'mui-color-input';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';

const UserCreateBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });
  const [formData, setFormData] = useState<{
    bannerTitle: string;
    message: string;
    bannerType: string;
    startDate: string | null;
    endDate: string | null;
    bannerActive: boolean;
    priority: number | '';
    bgColor: string;
    textColor: string;
  }>({
    bannerTitle: '',
    message: '',
    bannerType: '',
    startDate: dayjs().format('DD/MM/YYYY HH:mm'),
    endDate: dayjs().add(1, 'day').format('DD/MM/YYYY HH:mm'),
    bannerActive: true,
    priority: '',
    bgColor: '#000000',
    textColor: '#ffffff',
  });
  const isFormValid = () => {
    return (
      formData.bannerTitle.trim() !== '' &&
      formData.message.trim() !== '' &&
      formData.bannerType.trim() !== '' &&
      formData.priority !== '' &&
      formData.startDate !== null &&
      formData.endDate !== null &&
      dayjs(formData.startDate, 'DD/MM/YYYY HH:mm').isBefore(dayjs(formData.endDate, 'DD/MM/YYYY HH:mm')) &&
      formData.bgColor !== '' &&
      formData.textColor !== ''
    );
  };

  const {
    data: bannerCreate,
    error: bannerCreateError,
    loading: bannerCreateLoading,
    fetchData: fetchbannerCreateData,
  } = useApiError<any>(`${API_ENDPOINTS.users.USER_BANNER_CREATE}`, 'POST');

  const {
    data: bannerDataFetch,
    error: bannerDataFetchError,
    loading: bannerDataFetchLoading,
    fetchData: fetchbannerDataFetchData,
  } = useApiError<any>(`${API_ENDPOINTS.users.USER_BANNER_FETCH}`, 'POST');

  useEffect(() => {
    fetchbannerDataFetchData({ userId: user?.id });
  }, []);

  const priorityOptions = [
    { value: 0, label: 'Low' },
    { value: 1, label: 'Medium' },
    { value: 2, label: 'High' },
  ];

  const bannerTypeOption = [
    { value: 'info', label: 'Info' },
    { value: 'alert', label: 'Alert' },
    { value: 'announcement', label: 'Announcement' },
    { value: 'critical', label: 'Critical' },
  ];

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    const [datePart, timePart] = dateStr.split(' ');
    const [day, month, year] = datePart.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}:${timePart}:00`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    const payloads = {
      bannerTitle: formData.bannerTitle,
      message: formData.message,
      bannerType: formData.bannerType,
      startDate: formatDate(formData.startDate),
      endDate: formatDate(formData.endDate),
      bannerActive: formData.bannerActive,
      priority: formData.priority,
      bgColor: formData.bgColor,
      textColor: formData.textColor,
    };

    try {
      const response = await fetchbannerCreateData(payloads);
      if (response?.status === 200 || response?.status === 201) {
        setToast({ open: true, message: response.message, type: 'success' });
      }
      setFormData({
        bannerTitle: '',
        message: '',
        bannerType: '',
        startDate: dayjs().format('MM/DD/YYYY HH:mm'),
        endDate: dayjs().add(1, 'day').format('MM/DD/YYYY HH:mm'),
        bannerActive: true,
        priority: '',
        bgColor: '#000000',
        textColor: '#ffffff',
      });
      await fetchbannerDataFetchData({ userId: user?.id });
    } catch {
      setToast({ open: true, message: 'Error Creating Banner', type: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    if (bannerCreateError) {
      setToast({
        open: true,
        message: bannerCreateError as string,
        type: 'error',
      });
    }
  }, [bannerCreateError]);

  const refreshBannerData = () => {
    fetchbannerDataFetchData({ userId: user?.id });
  };

  return (
    <>
      <Box sx={{ width: '100%', display: 'flex', gap: '16px', flexDirection: 'column' }}>
        <div className="w-full">
          <button className="text-blue-600 font-medium flex items-center" onClick={() => navigate(-1)}>
            <ArrowBack className="mr-1" /> Back
          </button>
        </div>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            marginTop: '8px',
            marginBottom: '8px',
            border: '1px solid #D9D9D9',
            padding: '16px',
            borderRadius: '24px',
          }}
        >
          <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight="36px">
            Create Banner
          </Typography>
          <form onSubmit={handleSubmit} className="w-full flex flex-col gap-8">
            <Box className="w-full grid grid-cols-1 md:grid-cols-2 gap-3">
              <InputField
                label="Banner Title"
                name="bannerTitle"
                value={formData.bannerTitle}
                onChange={(e) => setFormData({ ...formData, bannerTitle: e.target.value })}
                placeholder="Enter banner title"
                isMandatory
              />
              <InputField
                label="Message"
                name="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Enter message"
                isMandatory
              />
              <CustomDropDownInput
                label="Banner Type"
                options={
                  bannerTypeOption?.map((type) => ({
                    value: type.value,
                    label: type.label,
                  })) || []
                }
                value={formData.bannerType}
                onChange={(val) => setFormData({ ...formData, bannerType: val })}
                placeholder="Select Banner Type"
                showDropdownArrow={true}
                isMandatory
              />
              <CustomDropDownInput
                label="Priority"
                options={
                  priorityOptions?.map((priority) => ({
                    value: priority.value,
                    label: priority.label,
                  })) || []
                }
                value={formData.priority}
                onChange={(val) => setFormData({ ...formData, priority: Number(val) })}
                placeholder="Select priority"
                showDropdownArrow={true}
                isMandatory
              />
              <Box className="flex flex-col gap-2">
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="textPrimary"
                  className="self-start flex items-center space-x-1"
                >
                  Background Color
                </Typography>
                <MuiColorInput
                  value={formData.bgColor}
                  onChange={(val: any) => setFormData({ ...formData, bgColor: val })}
                  className="mt-2 text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                />
              </Box>
              <Box className="flex flex-col gap-2">
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="textPrimary"
                  className="self-start flex items-center space-x-1"
                >
                  Text Color
                </Typography>
                <MuiColorInput
                  value={formData.textColor}
                  onChange={(val: any) => setFormData({ ...formData, textColor: val })}
                  className="mt-2 text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                />
              </Box>
            </Box>
            <Box className="w-full grid grid-cols-1 md:grid-cols-2 gap-3">
              <CustomDateTimePicker
                label="Start Date & Time"
                value={formData.startDate}
                onChange={(val) => setFormData({ ...formData, startDate: val })}
                is12HourFormat={false}
                isMandatory={true}
              />
              <CustomDateTimePicker
                label="End Date & Time"
                value={formData.endDate}
                onChange={(val) => setFormData({ ...formData, endDate: val })}
                is12HourFormat={false}
                isMandatory={true}
              />
            </Box>
            <Box
              sx={{
                width: '50%',
                display: 'flex',
                gap: '16px',
                justifyContent: 'flex-end',
                alignSelf: 'flex-end',
                alignItems: 'center',
              }}
            >
              <CustomIconButton
                text="Cancel"
                onClick={() => navigate(-1)}
                variant="outlined"
                isDisabled={isCreating}
              />
              <CustomIconButton
                text={isCreating ? 'Creating...' : 'Create'}
                onClick={handleSubmit}
                variant="contained"
                isDisabled={isCreating || !isFormValid()}
              />
            </Box>
          </form>
        </Box>
      </Box>
      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type as any}
      />
      <BannerDataTable bannerData={bannerDataFetch?.data} refreshData={refreshBannerData} />
    </>
  );
};

export default UserCreateBanner;
